import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./query-keys";
import {
  fetchUserProfile,
  loginWithGoogle,
  logoutFromFirebase,
  updateUserProfile,
  saveUserProfile,
  sendEmailSignInLink,
  completeEmailSignIn,
  type UserProfile,
} from "../api/auth";

// Query options for user profile
export const userProfileQueryOptions = (uid: string) =>
  queryOptions({
    queryKey: queryKeys.auth.profile(uid),
    queryFn: () => fetchUserProfile(uid),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

// Login mutation
export const useLoginMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const firebaseUser = await loginWithGoogle();
      const profile = await saveUserProfile(firebaseUser);
      return { firebaseUser, profile };
    },
    onSuccess: ({ profile }) => {
      // Cache the profile
      queryClient.setQueryData(queryKeys.auth.profile(profile.uid), profile);
    },
  });
};

// Logout mutation
export const useLogoutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logoutFromFirebase,
    onSuccess: () => {
      // Clear all cached data on logout
      queryClient.clear();
    },
  });
};

// Update profile mutation
export const useUpdateProfileMutation = (uid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profile: UserProfile) => updateUserProfile(profile),
    onSuccess: (_, variables) => {
      // Update the cached profile
      queryClient.setQueryData(queryKeys.auth.profile(uid), variables);
    },
  });
};

// Send email sign-in link mutation
export const useSendEmailLinkMutation = () => {
  return useMutation({
    mutationFn: sendEmailSignInLink,
  });
};

// Complete email sign-in mutation
export const useCompleteEmailSignInMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const firebaseUser = await completeEmailSignIn();
      const profile = await saveUserProfile(firebaseUser);
      return { firebaseUser, profile };
    },
    onSuccess: ({ profile }) => {
      queryClient.setQueryData(queryKeys.auth.profile(profile.uid), profile);
    },
  });
};
